import { isForageCrop } from '../pecuaria/pecuariaDomain';
import { toLocalIsoDate } from '../utils/localDate';
import { sumFieldCosts } from '../utils/fieldTaskCosts';
import { resolveOperationalPlotName } from '../services/operationalPropertiesService';

export const performanceKey = (plotId, cycleId) => cycleId ? `${plotId}:${cycleId}` : String(plotId);
export const hasValue = value => value != null && String(value).trim() !== '' && Number.isFinite(Number(value));
export const performanceDate = value => {
  if (!value) return '';
  return String(value).length === 10 ? String(value) : toLocalIsoDate(value);
};

export function validatePeriod(period) {
  const validDay = value => /^\d{4}-\d{2}-\d{2}$/.test(value || '')
    && !Number.isNaN(Date.parse(`${value}T12:00:00Z`))
    && new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value;
  if (!validDay(period.start) || !validDay(period.end)) return 'Informe duas datas válidas para o período.';
  if (period.start > period.end) return 'A data inicial não pode ser posterior à final.';
  return '';
}

export function currentYearPeriod() {
  const today = toLocalIsoDate();
  return { start: `${today.slice(0, 4)}-01-01`, end: today };
}

export function historyYears(dates) {
  return [...new Set(dates.map(performanceDate).filter(day => day && !validatePeriod({ start: day, end: day }))
    .map(day => day.slice(0, 4)))].sort().reverse();
}

export function previousPeriod(period) {
  if (validatePeriod(period)) return null;
  const start = Date.parse(`${period.start}T12:00:00Z`);
  const end = Date.parse(`${period.end}T12:00:00Z`);
  const day = 86400000;
  return {
    start: new Date(start - (end - start) - day).toISOString().slice(0, 10),
    end: new Date(start - day).toISOString().slice(0, 10),
  };
}

export function performanceChange(current, previous) {
  if (current == null || previous == null) return null;
  const difference = current - previous;
  return { difference, percent: previous === 0 ? null : difference / Math.abs(previous) * 100 };
}

export function cycleMetrics(row, estimate = {}, costs = {}) {
  const area = Number(row.area) || 0;
  const production = hasValue(estimate.produtividade) && Number(estimate.produtividade) > 0
    ? area * Number(estimate.produtividade) : null;
  const revenue = production != null && hasValue(estimate.cotacao) && Number(estimate.cotacao) > 0
    ? production * Number(estimate.cotacao) : null;
  const cost = Object.prototype.hasOwnProperty.call(costs, row._performanceKey) ? costs[row._performanceKey] : null;
  return {
    area, production, revenue, cost,
    margin: revenue != null && cost != null ? revenue - cost : null,
    realProduction: hasValue(estimate.real) ? area * Number(estimate.real) : null,
    breakEven: production > 0 && cost != null ? cost / production : null,
  };
}

export function inPeriod(date, period) {
  const day = performanceDate(date);
  return Boolean(day && !validatePeriod(period) && day >= period.start && day <= period.end);
}

export function cycleLifecycle(row, today = toLocalIsoDate()) {
  if (row._semPlantio) return 'available';
  if (row._ciclo?.status === 'colhido' && row.data_colheita_efetiva) return 'harvested';
  if (row.data_plantio > today) return 'planned';
  return 'active';
}

export function previousComparableCycle(row, rows) {
  if (!row) return null;
  const candidates = rows.filter(candidate => candidate._plotKey === row._plotKey
    && candidate.cultura === row.cultura && candidate.data_plantio < row.data_plantio
    && cycleLifecycle(candidate) === 'harvested')
    .sort((a, b) => b.data_plantio.localeCompare(a.data_plantio));
  if (!candidates.length || candidates[1]?.data_plantio === candidates[0].data_plantio) return null;
  return candidates[0];
}

export function buildPerformanceRows(plots, cycles) {
  return plots.flatMap(plot => {
    const ids = new Set([plot.id, ...(plot._legacyPlotIds || [])].map(String));
    const persisted = cycles.filter(cycle => ids.has(String(cycle.talhao_id)));
    const plantings = plot._operationalPhysical ? (plot.plantios || []) : [];
    const candidates = [
      ...(plot._operationalPhysical ? [] : persisted),
      ...plantings.map(planting => {
        const source = planting.fonte_legada === 'ciclos_cultivo'
          ? persisted.find(cycle => String(cycle.id) === String(planting.fonte_legada_id)) : null;
        const legacy = planting.fonte_legada === 'talhoes';
        return {
          ...planting,
          id: legacy ? null : source?.id || `plantio-${planting.id}`,
          talhao_id: source?.talhao_id || (legacy ? planting.fonte_legada_id : planting.talhao_legado_id) || plot.id,
          _readOnly: !source && !legacy,
          _legacy: legacy,
        };
      }),
      ...(!plot._operationalPhysical && !persisted.length && plot.data_plantio && plot.cultura ? [{
        id: null, talhao_id: plot.id, cultura: plot.cultura, data_plantio: plot.data_plantio,
        data_colheita: null, data_colheita_prevista: plot.data_colheita, _legacy: true,
      }] : []),
    ].filter(cycle => cycle.status !== 'cancelado' && cycle.data_plantio && !isForageCrop(cycle.cultura));
    if (!candidates.length) return [];
    return candidates.map(cycle => ({
      ...plot,
      id: cycle.talhao_id,
      _plotKey: String(plot._physicalPlotId || plot.id),
      cultura: cycle.cultura,
      data_plantio: cycle.data_plantio,
      data_colheita: cycle.data_colheita,
      data_colheita_prevista: cycle.data_colheita_prevista,
      data_colheita_efetiva: cycle.data_colheita,
      _ciclo: cycle._legacy ? null : cycle,
      _legacy: Boolean(cycle._legacy),
      _readOnly: Boolean(cycle._readOnly),
      _performanceKey: performanceKey(cycle.talhao_id, cycle.id),
    }));
  }).sort((a, b) => String(b.data_plantio).localeCompare(String(a.data_plantio)));
}

export function assignPerformanceCosts(entries, rows, plots = []) {
  const costs = {};
  const assignments = entries.map(entry => {
    const date = performanceDate(entry.date);
    const matches = rows.filter(row => !row._legacy && String(row.id) === String(entry.plotId)
      && date && date >= row.data_plantio
      && (!row.data_colheita_efetiva || date <= row.data_colheita_efetiva));
    const key = matches.length === 1 ? matches[0]._performanceKey : null;
    if (key) costs[key] = Math.round(((costs[key] || 0) + entry.value) * 100) / 100;
    return {
      ...entry,
      plotName: resolveOperationalPlotName(plots, entry.plotId) || entry.plotName || null,
      cycleKey: key,
      reason: key ? 'Associado por data e talhão'
        : entry.plotId == null ? 'Geral da propriedade'
          : matches.length > 1 ? 'Mais de um ciclo possível' : 'Sem ciclo agrícola compatível',
    };
  });
  return { costs, assignments, unassigned: assignments.filter(entry => !entry.cycleKey) };
}

export function summarizePerformance(rows, estimates, costs) {
  const summary = {
    area: 0, areaCultivada: 0, areaComMeta: 0, areaFinanceira: 0,
    receita: 0, custo: 0, custoFinanceiro: 0, comMeta: 0, comResultado: 0,
    metasAtingidas: 0, resultadosComparaveis: 0, comCustos: 0, comProjecao: 0, comProjecaoComCustos: 0,
    producaoPorUnidade: {}, producaoReal: {}, areaComResultado: {}, areaComMetaPorUnidade: {},
    custoProducaoReal: {}, producaoRealComCustos: {}, resultadosPorUnidade: {}, resultadosComCustosPorUnidade: {}, ciclos: rows.length,
  };
  const physicalAreas = new Map();
  rows.forEach(row => {
    const area = Number(row.area) || 0;
    physicalAreas.set(row._plotKey, area);
    summary.areaCultivada += area;
    const key = row._performanceKey;
    const expense = costs[key] || 0;
    summary.custo += Math.round(expense * 100);
    if (Object.prototype.hasOwnProperty.call(costs, key)) summary.comCustos++;
    const estimate = estimates[key];
    if (!estimate) return;
    const productionKey = `${row.cultura} (${estimate.unidade || 'sc'})`;
    if (hasValue(estimate.real)) {
      summary.comResultado++;
      summary.resultadosPorUnidade[productionKey] = (summary.resultadosPorUnidade[productionKey] || 0) + 1;
      summary.producaoReal[productionKey] = (summary.producaoReal[productionKey] || 0) + area * Number(estimate.real);
      summary.areaComResultado[productionKey] = (summary.areaComResultado[productionKey] || 0) + area;
      if (area > 0 && Object.prototype.hasOwnProperty.call(costs, key)) {
        summary.resultadosComCustosPorUnidade[productionKey] = (summary.resultadosComCustosPorUnidade[productionKey] || 0) + 1;
        summary.custoProducaoReal[productionKey] = (summary.custoProducaoReal[productionKey] || 0) + Math.round(expense * 100);
        summary.producaoRealComCustos[productionKey] = (summary.producaoRealComCustos[productionKey] || 0) + area * Number(estimate.real);
      }
    }
    if (hasValue(estimate.produtividade) && Number(estimate.produtividade) > 0) {
      const production = area * Number(estimate.produtividade);
      summary.producaoPorUnidade[productionKey] = (summary.producaoPorUnidade[productionKey] || 0) + production;
      summary.areaComMeta += area;
      summary.areaComMetaPorUnidade[productionKey] = (summary.areaComMetaPorUnidade[productionKey] || 0) + area;
      summary.comMeta++;
      if (hasValue(estimate.real)) {
        summary.resultadosComparaveis++;
        if (Number(estimate.real) >= Number(estimate.produtividade)) summary.metasAtingidas++;
      }
      if (hasValue(estimate.cotacao) && Number(estimate.cotacao) > 0) {
        summary.comProjecao++;
        if (Object.prototype.hasOwnProperty.call(costs, key)) summary.comProjecaoComCustos++;
        summary.receita += production * Number(estimate.cotacao);
        summary.custoFinanceiro += Math.round(expense * 100);
        summary.areaFinanceira += area;
      }
    }
  });
  summary.area = [...physicalAreas.values()].reduce((sum, area) => sum + area, 0);
  summary.custo /= 100;
  summary.custoFinanceiro /= 100;
  Object.keys(summary.custoProducaoReal).forEach(key => { summary.custoProducaoReal[key] /= 100; });
  summary.custoPorHa = rows.length && summary.comCustos === rows.length && rows.every(row => Number(row.area) > 0)
    ? summary.custo / summary.areaCultivada : null;
  summary.coberturaMeta = summary.areaCultivada ? summary.areaComMeta / summary.areaCultivada * 100 : 0;
  summary.coberturaFinanceira = summary.areaCultivada ? summary.areaFinanceira / summary.areaCultivada * 100 : 0;
  summary.margem = summary.comProjecao > 0 && summary.comProjecao === summary.comProjecaoComCustos
    ? summary.receita - summary.custoFinanceiro : null;
  return summary;
}

export function agriculturalComparison(current, previous, costsUnavailable) {
  const metrics = [
    { label: 'Custo por hectare', currency: true, current: costsUnavailable ? null : current.custoPorHa, previous: costsUnavailable ? null : previous.custoPorHa },
    { label: 'Área cultivada acumulada (ha)', current: current.areaCultivada, previous: previous.areaCultivada },
    { label: 'Gastos associados', currency: true, current: !costsUnavailable && current.comCustos ? current.custo : null, previous: !costsUnavailable && previous.comCustos ? previous.custo : null },
    { label: 'Receita projetada', currency: true, current: current.comProjecao ? current.receita : null, previous: previous.comProjecao ? previous.receita : null },
    { label: 'Saldo parcial projetado', currency: true, current: costsUnavailable ? null : current.margem, previous: costsUnavailable ? null : previous.margem },
  ];
  const products = new Set([...Object.keys(current.producaoReal), ...Object.keys(previous.producaoReal)]);
  products.forEach(product => {
    metrics.push({
      label: `Produtividade real média — ${product}/ha`,
      current: current.areaComResultado[product] > 0 ? current.producaoReal[product] / current.areaComResultado[product] : null,
      previous: previous.areaComResultado[product] > 0 ? previous.producaoReal[product] / previous.areaComResultado[product] : null,
    });
    metrics.push({
      label: `Custo por unidade produzida — ${product}`, currency: true,
      current: !costsUnavailable && current.resultadosPorUnidade[product] === current.resultadosComCustosPorUnidade[product] && current.producaoRealComCustos[product] > 0
        ? current.custoProducaoReal[product] / current.producaoRealComCustos[product] : null,
      previous: !costsUnavailable && previous.resultadosPorUnidade[product] === previous.resultadosComCustosPorUnidade[product] && previous.producaoRealComCustos[product] > 0
        ? previous.custoProducaoReal[product] / previous.producaoRealComCustos[product] : null,
    });
  });
  return metrics;
}

export function summarizeSales(sales) {
  return {
    revenue: sales.reduce((sum, sale) => sum + Math.round(Number(sale.valor_total) * 100), 0) / 100,
    animals: sales.reduce((sum, sale) => sum + Number(sale.quantidade_animais), 0),
  };
}

export function summarizePeriod(assignments, sales, period) {
  const entries = assignments.filter(entry => inPeriod(entry.date, period));
  const periodSales = sales.filter(sale => inPeriod(sale.vendido_em, period));
  return {
    entries,
    sales: periodSales,
    cost: sumFieldCosts(entries),
    generalCost: sumFieldCosts(entries.filter(entry => entry.plotId == null)),
    unassignedCost: sumFieldCosts(entries.filter(entry => entry.plotId != null && !entry.cycleKey)),
    assignedCost: sumFieldCosts(entries.filter(entry => entry.cycleKey)),
    revenue: summarizeSales(periodSales).revenue,
  };
}
